import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { StarBurst } from "../ui/StarBurst";
import type { TranslationQuestion } from "../levelTypes";

interface Props {
  question: TranslationQuestion;
  onAnswer: (result: "correct" | "hinted" | "wrong") => void;
}

type Phase = "idle" | "correct" | "wrong";

// Northern Sami special characters
const SAMI_CHARS = ["á", "Á", "č", "Č", "đ", "Đ", "ŋ", "Ŋ", "š", "Š", "ŧ", "Ŧ", "ž", "Ž"];

function normalize(s: string) {
  return s.trim().toLowerCase();
}

export function Translation({ question, onAnswer }: Props) {
  const [value, setValue] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [hintUsed, setHintUsed] = useState(false);
  const [hintText, setHintText] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const correctWord = question.word;

  function handleHint() {
    if (hintUsed) return;
    setHintUsed(true);
    // Show first 2 characters as hint
    const hint = question.hint ?? correctWord.slice(0, 2) + "…";
    setHintText(hint);
  }

  function insertChar(char: string) {
    const el = inputRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + char + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + char.length, start + char.length);
    });
  }

  function handleSubmit() {
    if (phase !== "idle" || value.trim() === "") return;

    const isCorrect = normalize(value) === normalize(correctWord);
    setPhase(isCorrect ? "correct" : "wrong");

    const result = isCorrect ? (hintUsed ? "hinted" : "correct") : "wrong";

    setTimeout(() => {
      onAnswer(result);
    }, 1500);
  }

  const inputClass =
    phase === "correct"
      ? "translation__input translation__input--correct"
      : phase === "wrong"
      ? "translation__input translation__input--wrong"
      : "translation__input";

  return (
    <div className="exercise-card">
      <StarBurst active={phase === "correct"} />

      <p className="translation__label">Oversett til samisk</p>
      <h2 className="translation__prompt">{question.translation}</h2>

      {/* Sami keyboard */}
      <div className="sami-keys" aria-label="Samiske spesialtegn">
        {SAMI_CHARS.map((ch) => (
          <button
            key={ch}
            className="sami-key"
            type="button"
            tabIndex={-1}
            disabled={phase !== "idle"}
            onClick={() => insertChar(ch)}
            aria-label={`Sett inn ${ch}`}
          >
            {ch}
          </button>
        ))}
      </div>

      {/* Input */}
      <motion.div
        className="translation__input-wrap"
        animate={
          phase === "wrong"
            ? { x: [0, -10, 10, -8, 8, -4, 4, 0] }
            : { x: 0 }
        }
        transition={{ duration: 0.4 }}
      >
        <input
          ref={inputRef}
          className={inputClass}
          type="text"
          value={phase === "wrong" ? correctWord : value}
          onChange={(e) => phase === "idle" && setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          placeholder="Skriv her…"
          disabled={phase !== "idle"}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Samisk oversettelse"
        />
        {hintText && phase === "idle" && (
          <span className="translation__hint-reveal" aria-live="polite">
            hint: {hintText}
          </span>
        )}
      </motion.div>

      {/* Actions */}
      <div className="translation__actions">
        <button
          className="btn-hint"
          type="button"
          onClick={handleHint}
          disabled={hintUsed || phase !== "idle"}
          aria-label="Vis hint"
        >
          💡 Hint
        </button>
        <button
          className="btn-submit"
          type="button"
          onClick={handleSubmit}
          disabled={value.trim() === "" || phase !== "idle"}
        >
          Sjekk
        </button>
      </div>

      {/* Wrong: show correct answer */}
      {phase === "wrong" && (
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: 12, color: "#9c7a4a", fontSize: 13 }}
        >
          Riktig svar:{" "}
          <strong style={{ color: "#f2ead8" }}>{correctWord}</strong>
        </motion.p>
      )}
    </div>
  );
}
